import { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { FARM_CONFIG, getMaxBounds } from "../config/farmConfig";
import { colorForKey } from "../lib/colors";
import { getCadastreInfoAtPoint } from "../lib/cadastre";
import { pointInPolygon } from "../lib/geo";

delete L.Icon.Default.prototype._getIconUrl;

function escapeHtml(s) {
  return (s ?? "").toString().replace(/[&<>"]/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c])
  );
}

function parcelPopupHtml(p) {
  const isXml = p.source === "xml";
  const rows = isXml
    ? [
        ["Culture", p.crop_label || p.crop],
        ["Surface", p.area != null ? `${p.area} ha` : ""],
        ["Commune", p.commune],
      ]
    : [
        ["Propriétaire", p.owner],
        ["Culture", p.crop_label || p.crop],
        ["Surface", p.area != null ? `${p.area} ha` : ""],
        ["Référence", p.immat || [p.section, p.numero].filter(Boolean).join(" ")],
        ["Commune", p.commune],
        ["Lieu-dit", p.lieu_dit],
        ["Convention", p.lease_type],
        ["Risques", p.risks],
        ["Notes", p.notes],
      ];
  const filtered = rows.filter(([, v]) => v);
  const hist = (p.crop_history || []).slice().sort((a, b) => b.year - a.year).slice(0, 6);
  return `
    <div class="popup-card">
      <div class="popup-title">${escapeHtml(p.name)}</div>
      ${filtered
        .map(([k, v]) => `<div class="popup-row"><span>${k}</span><strong>${escapeHtml(v)}</strong></div>`)
        .join("")}
      ${
        hist.length
          ? `<div style="margin-top:6px;padding-top:5px;border-top:1px solid #e5e0d5;">
              <div class="popup-title" style="font-size:12px;">Rotations</div>
              ${hist.map((h) => `<div class="popup-row"><span>${h.year}</span><strong>${escapeHtml(h.crop_label || h.crop)}</strong></div>`).join("")}
            </div>`
          : ""
      }
      ${isXml ? "" : '<div class="popup-conf">🔒 Données confidentielles</div>'}
    </div>`;
}

function cadastrePopupHtml(props, owned) {
  const rows = [
    ["Commune", props.nom_com || props.commune],
    ["Code INSEE", props.code_insee || props.code_com],
    ["Section", props.section],
    ["Numéro", props.numero],
    ["Contenance", props.contenance ? `${props.contenance} m²` : null],
  ].filter(([, v]) => v);
  const ownedRows = owned
    ? [
        ["Nom", owned.name],
        ["Propriétaire", owned.owner],
        ["Culture", owned.crop_label || owned.crop],
        ["Surface", owned.area != null ? `${owned.area} ha` : ""],
        ["Convention", owned.lease_type],
        ["Notes", owned.notes],
      ].filter(([, v]) => v)
    : [];
  return `
    <div class="popup-card">
      <div class="popup-title">Parcelle cadastrale</div>
      ${rows.map(([k, v]) => `<div class="popup-row"><span>${k}</span><strong>${escapeHtml(v)}</strong></div>`).join("")}
      ${
        ownedRows.length
          ? `<div style="margin-top:8px;padding-top:6px;border-top:1px solid #e5e0d5;">
              <div class="popup-title" style="font-size:12px;">Mes données</div>
              ${ownedRows.map(([k, v]) => `<div class="popup-row"><span>${k}</span><strong>${escapeHtml(v)}</strong></div>`).join("")}
              <div class="popup-conf">🔒 Données confidentielles</div>
            </div>`
          : ""
      }
    </div>`;
}

function normRef(v) {
  return (v ?? "").toString().trim().toUpperCase().replace(/^0+/, "");
}

function findOwnedParcel(latlng, props, parcels) {
  const mine = parcels.filter((p) => p.source !== "xml");
  const pt = [latlng.lat, latlng.lng];
  const byGeom = mine.find((p) => p.polygon && p.polygon.length >= 3 && pointInPolygon(pt, p.polygon));
  if (byGeom) return byGeom;
  if (!props) return null;
  const insee = (props.code_insee || props.code_com || "").toString();
  return (
    mine.find(
      (p) =>
        p.section &&
        p.numero &&
        normRef(p.section) === normRef(props.section) &&
        normRef(p.numero) === normRef(props.numero) &&
        (!p.code_insee || !insee || normRef(p.code_insee) === normRef(insee))
    ) || null
  );
}

export default function MapView({
  parcels,
  showAerial,
  showCadastre,
  showParcels,
  showCultures,
  selectedId,
  fitTrigger,
  drawParcelId,
  onPolygonDrawn,
}) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const aerialRef = useRef(null);
  const baseLightRef = useRef(null);
  const cadastreRef = useRef(null);
  const parcelsLayerRef = useRef(null);
  const parcelShapesRef = useRef({});
  const drawRef = useRef({ active: false, points: [], layer: null, markers: [] });
  const onPolygonDrawnRef = useRef(onPolygonDrawn);
  onPolygonDrawnRef.current = onPolygonDrawn;
  const parcelsRef = useRef(parcels);
  parcelsRef.current = parcels;

  useEffect(() => {
    if (mapRef.current) return;

    const map = L.map(containerRef.current, {
      center: FARM_CONFIG.center,
      zoom: FARM_CONFIG.defaultZoom,
      minZoom: FARM_CONFIG.minZoom,
      maxZoom: FARM_CONFIG.maxZoom,
      maxBounds: getMaxBounds(),
      maxBoundsViscosity: 1.0,
    });
    mapRef.current = map;

    aerialRef.current = L.tileLayer(
      "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
      { maxZoom: 19, attribution: "Imagerie © Esri" }
    );
    baseLightRef.current = L.tileLayer(
      "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png",
      { maxZoom: 19, attribution: "© OpenStreetMap, © CARTO" }
    );
    cadastreRef.current = L.tileLayer.wms("https://data.geopf.fr/wms-r/wms", {
      layers: "CADASTRALPARCELS.PARCELLAIRE_EXPRESS",
      format: "image/png",
      transparent: true,
      version: "1.3.0",
      maxZoom: 19,
      attribution: "Cadastre © DGFiP / IGN",
    });
    parcelsLayerRef.current = L.layerGroup();

    L.control.scale({ imperial: false, position: "bottomleft" }).addTo(map);

    const NorthArrow = L.Control.extend({
      options: { position: "topright" },
      onAdd: function () {
        const div = L.DomUtil.create("div", "north-arrow");
        div.innerHTML = '<div class="north-n">N</div><div class="north-tri"></div>';
        return div;
      },
    });
    map.addControl(new NorthArrow());

    const updateLabelVisibility = () => {
      map.getContainer().classList.toggle("hide-parcel-labels", map.getZoom() < 14);
    };
    map.on("zoomend", updateLabelVisibility);
    updateLabelVisibility();

    map.on("click", async (e) => {
      // Mode dessin : ajoute un sommet
      if (drawRef.current.active) {
        const d = drawRef.current;
        d.points.push([e.latlng.lat, e.latlng.lng]);
        const m = L.circleMarker(e.latlng, { radius: 5, color: "#e8c872", fillColor: "#1a4731", fillOpacity: 1, weight: 2 }).addTo(map);
        d.markers.push(m);
        if (d.layer) d.layer.setLatLngs(d.points);
        else d.layer = L.polygon(d.points, { color: "#e8c872", weight: 2, dashArray: "5 4", fillOpacity: 0.2 }).addTo(map);
        return;
      }
      if (!cadastreRef.current || !map.hasLayer(cadastreRef.current)) return;
      const popup = L.popup({ maxWidth: 280 }).setLatLng(e.latlng).setContent('<div class="popup-card">Chargement du cadastre…</div>').openOn(map);
      const info = await getCadastreInfoAtPoint(e.latlng);
      const owned = findOwnedParcel(e.latlng, info?.properties, parcelsRef.current);
      if (info && info.properties) {
        popup.setContent(cadastrePopupHtml(info.properties, owned));
      } else if (owned) {
        popup.setContent(parcelPopupHtml(owned));
      } else {
        popup.setContent('<div class="popup-card">Aucune parcelle cadastrale ici.</div>');
      }
    });

    map.on("dblclick", (e) => {
      if (!drawRef.current.active) return;
      L.DomEvent.stop(e);
      finishDrawing();
    });

    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, []);

  function clearDrawing() {
    const map = mapRef.current;
    const d = drawRef.current;
    if (d.layer && map) map.removeLayer(d.layer);
    d.markers.forEach((m) => map && map.removeLayer(m));
    drawRef.current = { active: false, points: [], layer: null, markers: [] };
  }

  function finishDrawing() {
    const d = drawRef.current;
    const pts = d.points.slice();
    clearDrawing();
    if (pts.length >= 3 && onPolygonDrawnRef.current) onPolygonDrawnRef.current(pts);
    else if (onPolygonDrawnRef.current) onPolygonDrawnRef.current(null);
  }

  // Active / désactive le mode dessin
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (drawParcelId) {
      clearDrawing();
      drawRef.current.active = true;
      map.doubleClickZoom.disable();
      map.getContainer().style.cursor = "crosshair";
    } else {
      clearDrawing();
      map.doubleClickZoom.enable();
      map.getContainer().style.cursor = "";
    }
  }, [drawParcelId]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (showAerial) {
      if (map.hasLayer(baseLightRef.current)) map.removeLayer(baseLightRef.current);
      if (!map.hasLayer(aerialRef.current)) aerialRef.current.addTo(map);
      aerialRef.current.bringToBack();
    } else {
      if (map.hasLayer(aerialRef.current)) map.removeLayer(aerialRef.current);
      if (!map.hasLayer(baseLightRef.current)) baseLightRef.current.addTo(map);
      baseLightRef.current.bringToBack();
    }
  }, [showAerial]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (showCadastre) {
      if (!map.hasLayer(cadastreRef.current)) cadastreRef.current.addTo(map);
    } else if (map.hasLayer(cadastreRef.current)) {
      map.removeLayer(cadastreRef.current);
    }
  }, [showCadastre]);

  // Rendu des parcelles (cadastre/manuel + cultures Télépac)
  useEffect(() => {
    const map = mapRef.current;
    const group = parcelsLayerRef.current;
    if (!map || !group) return;

    group.clearLayers();
    parcelShapesRef.current = {};

    const sorted = [...parcels].sort((a, b) => (a.source === "xml" ? -1 : 0) - (b.source === "xml" ? -1 : 0));
    sorted.forEach((p) => {
      if (!p.polygon || p.polygon.length < 3) return;
      const isXml = p.source === "xml";
      if (isXml && !showCultures) return;
      if (!isXml && !showParcels) return;

      const color = colorForKey(p.crop_label || p.crop || p.owner || p.name);

      const poly = L.polygon(p.polygon, {
        color: isXml ? color : "#ffffff",
        weight: isXml ? 1.5 : 2,
        dashArray: isXml ? "6 4" : null,
        fillColor: color,
        fillOpacity: isXml ? 0.5 : 0.65,
      });
      poly.bindPopup(parcelPopupHtml(p), { maxWidth: 280 });
      poly.bindTooltip(p.name, {
        permanent: true,
        direction: "center",
        className: "parcel-label",
      });
      poly.on("click", (e) => L.DomEvent.stopPropagation(e));
      group.addLayer(poly);
      parcelShapesRef.current[p.id] = poly;
    });

    if (!map.hasLayer(group)) group.addTo(map);
  }, [parcels, showParcels, showCultures]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !selectedId) return;
    const shape = parcelShapesRef.current[selectedId];
    if (!shape) return;
    map.flyToBounds(shape.getBounds(), { maxZoom: FARM_CONFIG.maxZoom, padding: [40, 40] });
    shape.openPopup();
  }, [selectedId]);

  useEffect(() => {
    const map = mapRef.current;
    const group = parcelsLayerRef.current;
    if (!map || !group || !fitTrigger) return;
    const located = parcels.filter((p) => p.polygon && p.polygon.length >= 3);
    if (located.length === 0) return;
    try {
      const bounds = group.getBounds();
      if (bounds.isValid()) {
        map.flyToBounds(bounds, { maxZoom: FARM_CONFIG.maxZoom, padding: [50, 50] });
        map.setMaxBounds(bounds.pad(0.6));
        const targetZoom = map.getBoundsZoom(bounds);
        map.setMinZoom(Math.max(11, Math.min(13, targetZoom - 2)));
      }
    } catch (e) {
      /* noop */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fitTrigger]);

  return <div ref={containerRef} data-testid="farm-map" className="h-full w-full" />;
}
